/**
 * A flow in plain words, and where its blocks sit on the canvas — pure.
 *
 * The canvas is drawn from the flow definition (`functions-webhook/src/
 * automation/flows.ts`), never stored: what the screen shows is what the engine
 * runs. Kept free of React so `scripts/checkAutomation.ts` can check the layout.
 */
import {
  EVENT_LABELS,
  isWired,
  type Block,
  type Flow,
  type Reentry,
  type Rule,
  type Trigger,
} from '../../../../functions-webhook/src/automation/flows'

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/**
 * Whether anything can start this flow today — 14 Oct 2026.
 *
 * Events (RevenueCat, and new accounts read by the scheduler) and calendar
 * dates (released in daily batches). Season-relative and inactivity starts
 * wait for the app's own events, and say so.
 */
export const canStart = (f: Flow): boolean =>
  isWired(f) && (f.trigger.kind === 'event' || f.trigger.kind === 'date')

export const REENTRY_LABEL: Record<Reentry, string> = {
  user: 'Once per contact',
  trial: 'Once per trial',
  subscription: 'Once per subscription period',
  season: 'Once per season',
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** `11-01` → `1 November`. */
export function dayMonth(mmdd: string): string {
  const [m, d] = mmdd.split('-').map(Number)
  return `${d} ${MONTHS[(m ?? 1) - 1]}`
}

function duration(hours: number): string {
  const h = Math.abs(hours)
  if (h % 24 === 0) {
    const d = h / 24
    return d === 7 ? '1 week' : `${d} day${d === 1 ? '' : 's'}`
  }
  return `${h} hour${h === 1 ? '' : 's'}`
}

/** What the end date an event carries is, in words. */
const ANCHOR: Partial<Record<string, string>> = {
  'trial.started': 'the trial ends',
  'subscription.renewed': 'the next renewal',
  'subscription.started': 'the next renewal',
}

/** "1 day before the trial ends", "When it happens", "7 days after". */
export function timingOf(t: Extract<Trigger, { kind: 'event' }>): string {
  const off = t.offsetHours ?? 0
  if (t.from === 'expiresAt') {
    const anchor = ANCHOR[t.event] ?? 'the end date'
    if (off === 0) return `When ${anchor}`
    return `${duration(off)} ${off < 0 ? 'before' : 'after'} ${anchor}`
  }
  if (off === 0) return 'When it happens'
  return `${duration(off)} ${off < 0 ? 'before' : 'after'}`
}

export function triggerKind(t: Trigger): string {
  switch (t.kind) {
    case 'event':
      return 'Trigger · event'
    case 'date':
      return 'Trigger · date'
    case 'before-season':
      return 'Trigger · season'
    case 'monthly-in-season':
      return 'Trigger · monthly'
    case 'inactive':
      return 'Trigger · inactivity'
  }
}

export function ruleText(r: Rule): string {
  switch (r.field) {
    case 'plan': {
      const plan = r.value === 'pro' ? 'PRO' : r.value[0]!.toUpperCase() + r.value.slice(1)
      return `Plan ${r.op === 'is' ? 'is' : 'is not'} ${plan}`
    }
    case 'autoRenew':
      return r.value ? 'Auto-renew is on' : 'Auto-renew is off'
    case 'billingIssue':
      return r.value ? 'A payment is failing' : 'No payment is failing'
    case 'marketingConsent':
      return r.value ? 'Has marketing consent' : 'No marketing consent'
    case 'event':
      return `Has not yet: ${EVENT_LABELS[r.value].toLowerCase()} (since the flow started)`
    case 'sent':
      return `Did not get ${r.value} in the last ${r.days} days`
    case 'inactiveDays':
      return r.op === 'at-least'
        ? `Has not opened the app for at least ${r.value} days`
        : `Opened the app in the last ${r.value} days`
    case 'trialEndsInDays':
      return `The trial has at least ${r.value} days left`
  }
}

export function blockKind(b: Block): string {
  switch (b.kind) {
    case 'wait':
      return 'Wait'
    case 'batch':
      return 'Wait · batch'
    case 'condition':
      return 'Condition'
    case 'send':
      return 'Send email'
    case 'task':
      return 'Create task'
    case 'exit':
      return 'Exit'
  }
}

export function blockTitle(b: Block): string {
  switch (b.kind) {
    case 'wait':
      return 'hours' in b ? `Wait ${duration(b.hours)}` : 'Until the send window'
    case 'batch':
      return 'Send in daily batches'
    case 'condition':
      return b.label
    case 'send':
      return b.subject
    case 'task':
      return b.label
    case 'exit':
      return b.reason
  }
}

// ---------------------------------------------------------------------------
// Counts
// ---------------------------------------------------------------------------

/** One count line on a block: label and the stats key it reads. */
export interface CountSpec {
  label: string
  /** A key in the day documents, or `live` for a count of runs waiting now. */
  key: string | 'live'
}

export function countsOf(item: Placed['item']): CountSpec[] {
  if (item === 'trigger') return [{ label: 'Entered', key: 'trigger__entered' }]
  switch (item.kind) {
    case 'wait':
      return [{ label: 'Waiting', key: 'live' }]
    case 'batch':
      return [{ label: 'Waiting for a batch', key: 'live' }]
    case 'condition':
      return [
        { label: 'Yes', key: `${item.id}__yes` },
        { label: 'No', key: `${item.id}__no` },
      ]
    case 'send':
      return [
        { label: 'Sent', key: `${item.id}__sent` },
        { label: 'Opened', key: `${item.id}__opened` },
        { label: 'Clicked', key: `${item.id}__clicked` },
      ]
    case 'task':
      return [{ label: 'Created', key: `${item.id}__created` }]
    case 'exit':
      return [{ label: 'Exited', key: `${item.id}__exited` }]
  }
}

// ---------------------------------------------------------------------------
// Layout — artboard 6's rules
// ---------------------------------------------------------------------------
//
// One column, top down, 48 px between blocks (72 after a condition, for the
// branch label). Main blocks are 304 wide; the exit a condition's No leads to
// sits to the right, 224 wide, level with the block after the condition.

export const MAIN_X = 168
export const MAIN_W = 304
export const SIDE_X = 536
export const SIDE_W = 224
export const STAGE_W = 824
const TOP = 48
const GAP = 48
const GAP_AFTER_CONDITION = 72

export interface Placed {
  id: string
  item: Block | 'trigger'
  x: number
  y: number
  w: number
  h: number
  side: boolean
}

export interface Edge {
  from: string
  to: string
  /** Straight down, or out of the right edge and down into a side exit. */
  shape: 'down' | 'right'
  label?: 'Yes' | 'No'
  /** For a `right` edge: the y it leaves the condition at. */
  y1: number
  x1: number
  x2: number
  y2: number
}

function heightOf(item: Block | 'trigger', side: boolean): number {
  if (side) return 64
  if (item === 'trigger') return 96
  switch (item.kind) {
    case 'condition':
      return 104
    case 'send':
      return 184
    case 'exit':
      return 64
    default:
      return 96
  }
}

export function layoutFlow(flow: Flow): { placed: Placed[]; edges: Edge[]; height: number } {
  const sideIds = new Set(
    flow.blocks.flatMap((b) => (b.kind === 'condition' ? [b.no] : [])),
  )
  const main: (Block | 'trigger')[] = ['trigger', ...flow.blocks.filter((b) => !sideIds.has(b.id))]
  const placed: Placed[] = []
  const edges: Edge[] = []

  let y = TOP
  for (const item of main) {
    const h = heightOf(item, false)
    placed.push({ id: item === 'trigger' ? 'trigger' : item.id, item, x: MAIN_X, y, w: MAIN_W, h, side: false })
    y += h + (item !== 'trigger' && item.kind === 'condition' ? GAP_AFTER_CONDITION : GAP)
  }

  // Straight edges down the main column. An exit ends the line.
  for (let i = 0; i < placed.length - 1; i++) {
    const a = placed[i]!
    const b = placed[i + 1]!
    if (a.item !== 'trigger' && a.item.kind === 'exit') continue
    const isCondition = a.item !== 'trigger' && a.item.kind === 'condition'
    edges.push({
      from: a.id,
      to: b.id,
      shape: 'down',
      label: isCondition ? 'Yes' : undefined,
      x1: MAIN_X + MAIN_W / 2,
      y1: a.y + a.h,
      x2: MAIN_X + MAIN_W / 2,
      y2: b.y,
    })
  }

  // Side exits, level with the block after their condition.
  let bottom = y - GAP
  for (const c of flow.blocks) {
    if (c.kind !== 'condition') continue
    const target = flow.blocks.find((b) => b.id === c.no)
    if (!target) continue
    const cond = placed.find((p) => p.id === c.id)
    if (!cond) continue
    let exit = placed.find((p) => p.id === target.id)
    if (!exit) {
      const after = placed[placed.indexOf(cond) + 1]
      const h = heightOf(target, true)
      exit = { id: target.id, item: target, x: SIDE_X, y: after ? after.y : cond.y + cond.h + GAP, w: SIDE_W, h, side: true }
      placed.push(exit)
      bottom = Math.max(bottom, exit.y + h)
    }
    edges.push({
      from: c.id,
      to: exit.id,
      shape: 'right',
      label: 'No',
      x1: MAIN_X + MAIN_W,
      y1: cond.y + cond.h / 2,
      x2: exit.x + exit.w / 2,
      y2: exit.y,
    })
  }

  return { placed, edges, height: bottom + TOP }
}

// ---------------------------------------------------------------------------
// Merge tags in a template
// ---------------------------------------------------------------------------

const TAG = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*(?:\|([^}]*))?\}\}/g
/** Filled per recipient by the footer, not by merge — never "missing". */
const FOOTER = new Set(['unsubscribe', 'preferences'])

/** Every merge tag a template uses, as written: `firstname|there`, `expires`. */
export function tagsIn(text: string): { tag: string; shown: string }[] {
  const seen = new Map<string, string>()
  for (const m of text.matchAll(TAG)) {
    const tag = m[1]!
    if (FOOTER.has(tag) || seen.has(tag)) continue
    seen.set(tag, m[2] !== undefined ? `${tag}|${m[2].trim()}` : tag)
  }
  return [...seen].map(([tag, shown]) => ({ tag, shown }))
}
